import api from './api';
import { translateError } from '../utils/errorTranslations';

/**
 * Calcula o Índice de Estresse Interno Dinâmico (SID) Enhanced BIDIRECIONAL com Modulação ROC
 * @param {number} rsi - Valor do RSI atual (0-100)
 * @param {string} regime - Regime de mercado (bear, transition, pre_bull, bull)
 * @param {number|null} currentPrice - Preço atual do Bitcoin (opcional, para ativar ROC Enhancement)
 * @returns {Promise<Object>} Dados do SID bidirecional (-1.0 a +1.0) com ranges Q1/Q4, ROC data e enhancement info
 */
export const calculateSID = async (rsi, regime, currentPrice = null) => {
  try {
    // Validação de entrada seguindo padrões do projeto
    if (!rsi || !regime) {
      throw new Error(translateError('RSI e regime são obrigatórios'));
    }

    const payload = {
      rsi: parseFloat(rsi),
      regime: regime.toLowerCase()
    };

    // Adicionar current_price se fornecido para ativar ROC Enhancement
    if (currentPrice && currentPrice > 0) {
      payload.current_price = parseFloat(currentPrice);
      console.log('Calculando SID Enhanced:', payload);
    } else {
      console.log('Calculando SID (base):', payload);
    }
    
    const { data } = await api.post('/dynamic-stress/calculate/', payload);
    
    // Log detalhado para novos campos bidirecionais
    if (data.enhancement && data.enhancement.enhancement_active !== false) {
      console.log('SID Enhanced Bidirecional calculado:', {
        sid_enhanced: data.sid_value,
        sid_base: data.enhancement.sid_base,
        enhancement_type: data.enhancement.enhancement_type,
        min_roc: data.roc_data.min_roc,
        max_roc: data.roc_data.max_roc,
        crash_modulation: data.roc_data.crash_modulation,
        rally_modulation: data.roc_data.rally_modulation
      });
    } else {
      console.log('SID Bidirecional (base) calculado:', {
        sid_value: data.sid_value,
        stress_level: data.stress_level,
        ranges: data.bidirectional_ranges
      });
    }
    
    return data;
  } catch (error) {
    console.error('Error calculating SID:', error);
    console.error('Response status:', error.response?.status);
    console.error('Response data:', error.response?.data);
    
    // ✅ CORREÇÃO: Preservar tipo de erro para tratamento específico
    if (error.name === 'AxiosError' && error.code === 'ECONNABORTED') {
      console.log('SID calculation was aborted (likely due to page reload)');
      // Preservar o erro original para que possa ser detectado no MarketContext
      throw error;
    }
    
    throw new Error(error.response?.data?.detail || translateError('Erro ao calcular índice de estresse'));
  }
};

/**
 * Obtém informações sobre as faixas Q1 (estresse) de um regime específico
 * @param {string} regime - Regime de mercado
 * @returns {Promise<Object>} Informações do Q1 do regime
 */
export const getRegimeStressInfo = async (regime) => {
  try {
    if (!regime) {
      throw new Error(translateError('Regime é obrigatório'));
    }

    const { data } = await api.get(`/dynamic-stress/regime/${regime.toLowerCase()}/`);
    return data;
  } catch (error) {
    console.error('Error getting regime stress info:', error);
    throw new Error(error.response?.data?.detail || translateError('Erro ao obter informações do regime'));
  }
};

/**
 * Obtém informações sobre as faixas Q4 (sobrecompra) de um regime específico
 * @param {string} regime - Regime de mercado
 * @returns {Promise<Object>} Informações do Q4 do regime
 */
export const getRegimeOverextendedInfo = async (regime) => {
  try {
    if (!regime) {
      throw new Error(translateError('Regime é obrigatório'));
    }

    const { data } = await api.get(`/dynamic-stress/regime/${regime.toLowerCase()}/q4/`);
    return data;
  } catch (error) {
    console.error('Error getting regime overextended info:', error);
    throw new Error(error.response?.data?.detail || translateError('Erro ao obter informações de sobrecompra do regime'));
  }
};

/**
 * Obtém informações BIDIRECIONAIS completas de um regime específico (Q1 + Q4 + zona neutra)
 * @param {string} regime - Regime de mercado
 * @returns {Promise<Object>} Informações bidirecionais completas do regime
 */
export const getRegimeBidirectionalInfo = async (regime) => {
  try {
    if (!regime) {
      throw new Error(translateError('Regime é obrigatório'));
    }

    const { data } = await api.get(`/dynamic-stress/regime/${regime.toLowerCase()}/bidirectional/`);
    return data;
  } catch (error) {
    console.error('Error getting regime bidirectional info:', error);
    throw new Error(error.response?.data?.detail || translateError('Erro ao obter informações bidirecionais do regime'));
  }
};

/**
 * Obtém informações BIDIRECIONAIS para todos os regimes
 * @returns {Promise<Object>} Informações bidirecionais de todos os regimes
 */
export const getAllRegimesStressInfo = async () => {
  try {
    const { data } = await api.get('/dynamic-stress/regimes/');
    return data;
  } catch (error) {
    console.error('Error getting all regimes stress info:', error);
    throw new Error(error.response?.data?.detail || translateError('Erro ao obter informações dos regimes'));
  }
};

/**
 * Verifica a saúde do serviço Dynamic Stress
 * @returns {Promise<Object>} Status de saúde do serviço
 */
export const checkDynamicStressHealth = async () => {
  try {
    const { data } = await api.get('/dynamic-stress/health/');
    return data;
  } catch (error) {
    console.error('Error checking dynamic stress health:', error);
    throw new Error(error.response?.data?.detail || translateError('Erro ao verificar serviço'));
  }
};

/**
 * Interpreta o nível de prioridade SID em texto amigável
 * @param {string} priority - Prioridade retornada pela API
 * @returns {string} Texto interpretado
 */
export const interpretStressPriority = (priority) => {
  const priorities = {
    'EXCEPTIONAL': 'Excepcional',
    'EXCELLENT': 'Excelente', 
    'GOOD': 'Bom',
    'CONSIDER': 'Considerar',
    'WAIT': 'Aguardar',
    'ERROR': 'Erro'
  };
  
  return priorities[priority] || priority;
};

/**
 * Interpreta o tipo de enhancement ROC BIDIRECIONAL em texto amigável
 * @param {string} enhancementType - Tipo de enhancement retornado pela API
 * @returns {string} Texto interpretado
 */
export const interpretEnhancementType = (enhancementType) => {
  const types = {
    'CRASH_AMPLIFY_STRESS': 'ROC Crash Amplificando Estresse',
    'RALLY_AMPLIFY_OVEREXTENDED': 'ROC Rally Amplificando Sobrecompra',
    'CRASH_BOOST_FROM_NEUTRAL': 'ROC Crash Detectado (Zona Neutra)',
    'RALLY_BOOST_FROM_NEUTRAL': 'ROC Rally Detectado (Zona Neutra)',
    'NO_ENHANCEMENT': 'Sem Enhancement ROC',
    'ERROR': 'Erro no Enhancement'
  };
  
  return types[enhancementType] || enhancementType;
};

/**
 * Verifica se o SID Enhanced BIDIRECIONAL está ativo
 * @param {Object} sidData - Dados retornados pela API
 * @returns {boolean} True se enhancement está ativo
 */
export const isEnhancementActive = (sidData) => {
  return sidData?.enhancement?.enhancement_active !== false && 
         sidData?.enhancement?.enhancement_type && 
         !sidData.enhancement.enhancement_type.includes('ERROR') &&
         !sidData.enhancement.enhancement_type.includes('NO_ENHANCEMENT');
};

/**
 * Obtém informações sobre as faixas bidirecionais (Q1 + Q4 + zona neutra)
 * @param {Object} sidData - Dados retornados pela API
 * @returns {Object|null} Informações das faixas bidirecionais
 */
export const getBidirectionalRanges = (sidData) => {
  if (!sidData?.bidirectional_ranges) return null;
  
  const ranges = sidData.bidirectional_ranges;
  return {
    q1_stress: ranges.q1_stress,
    q4_overextended: ranges.q4_overextended,
    neutral_zone: ranges.neutral_zone,
    // Informações calculadas
    total_range: ranges.q4_overextended.max - ranges.q1_stress.min,
    stress_percentage: ((ranges.q1_stress.max - ranges.q1_stress.min) / (ranges.q4_overextended.max - ranges.q1_stress.min)) * 100,
    overextended_percentage: ((ranges.q4_overextended.max - ranges.q4_overextended.min) / (ranges.q4_overextended.max - ranges.q1_stress.min)) * 100,
    neutral_percentage: (ranges.neutral_zone.gap / (ranges.q4_overextended.max - ranges.q1_stress.min)) * 100
  };
};

/**
 * Determina o tipo de indicação do SID (estresse, neutro, sobrecompra)
 * @param {number} sidValue - Valor do SID (-1.0 a +1.0)
 * @returns {Object} Informações sobre o tipo de indicação
 */
export const getSidIndicationType = (sidValue) => {
  if (sidValue > 0) {
    return {
      type: 'stress',
      intensity: sidValue,
      description: 'Indicação de Estresse',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      icon: '📉'
    };
  } else if (sidValue < 0) {
    return {
      type: 'overextended',
      intensity: Math.abs(sidValue),
      description: 'Indicação de Sobrecompra',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      icon: '📈'
    };
  } else {
    return {
      type: 'neutral',
      intensity: 0,
      description: 'Zona Neutra',
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      icon: '⚪'
    };
  }
};

/**
 * Obtém informações detalhadas sobre o ROC BIDIRECIONAL
 * @param {Object} sidData - Dados retornados pela API
 * @returns {Object} Informações formatadas do ROC bidirecional
 */
export const getROCInfo = (sidData) => {
  if (!sidData?.roc_data) return null;
  
  const roc = sidData.roc_data;
  return {
    min_roc: roc.min_roc,
    max_roc: roc.max_roc,
    min_roc_formatted: `${roc.min_roc > 0 ? '+' : ''}${roc.min_roc.toFixed(1)}%`,
    max_roc_formatted: `${roc.max_roc > 0 ? '+' : ''}${roc.max_roc.toFixed(1)}%`,
    roc_7d_formatted: `${roc.roc_7d > 0 ? '+' : ''}${roc.roc_7d.toFixed(1)}%`,
    roc_15d_formatted: `${roc.roc_15d > 0 ? '+' : ''}${roc.roc_15d.toFixed(1)}%`,
    roc_30d_formatted: `${roc.roc_30d > 0 ? '+' : ''}${roc.roc_30d.toFixed(1)}%`,
    crash_modulation: roc.crash_modulation,
    rally_modulation: roc.rally_modulation,
    crash_modulation_formatted: `${(roc.crash_modulation * 100).toFixed(1)}%`,
    rally_modulation_formatted: `${(roc.rally_modulation * 100).toFixed(1)}%`,
    crash_timeframe: roc.crash_timeframe,
    rally_timeframe: roc.rally_timeframe,
    // Manter compatibilidade com código existente
    modulation: Math.max(roc.crash_modulation, roc.rally_modulation),
    modulation_formatted: `${(Math.max(roc.crash_modulation, roc.rally_modulation) * 100).toFixed(1)}%`
  };
};

/**
 * Interpreta o nível de estresse/sobrecompra BIDIRECIONAL em cores para UI
 * Suporta níveis de estresse (SID positivo) e sobrecompra (SID negativo)
 * @param {string} stressLevel - Nível retornado pela API
 *   Estresse: EXTREME_STRESS, MAXIMUM_STRESS, HIGH_STRESS, MODERATE_STRESS, LOW_STRESS, MINIMAL_STRESS
 *   Enhanced: *_ENHANCED (com ROC Enhancement ativo)
 *   Sobrecompra: EXTREME_OVEREXTENDED, MAXIMUM_OVEREXTENDED, HIGH_OVEREXTENDED, MODERATE_OVEREXTENDED, LOW_OVEREXTENDED, MINIMAL_OVEREXTENDED
 *   Neutro: NEUTRAL
 * @returns {Object} Configuração de cores e estilo com bgColor, borderColor, iconBg, iconColor, textColor, emoji
 */
export const getStressLevelStyle = (stressLevel) => {
  // Normalizar: tratar *_ENHANCED como a mesma fase base
  const normalized = typeof stressLevel === 'string' ? stressLevel.replace('_ENHANCED', '') : stressLevel;
  const styles = {
    // ========== NÍVEIS DE ESTRESSE (SID POSITIVO) ==========
    'EXTREME_STRESS': {
      bgColor: 'from-red-50 to-pink-50',
      borderColor: 'border-red-200',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      textColor: 'text-red-900',
      emoji: '🔥'
    },
    'MAXIMUM_STRESS': {
      bgColor: 'from-orange-50 to-red-50',
      borderColor: 'border-orange-200', 
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      textColor: 'text-orange-900',
      emoji: '⚡'
    },
    'HIGH_STRESS': {
      bgColor: 'from-yellow-50 to-orange-50',
      borderColor: 'border-yellow-200',
      iconBg: 'bg-yellow-100', 
      iconColor: 'text-yellow-600',
      textColor: 'text-yellow-900',
      emoji: '📈'
    },
    'MODERATE_STRESS': {
      bgColor: 'from-blue-50 to-indigo-50',
      borderColor: 'border-blue-200',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600', 
      textColor: 'text-blue-900',
      emoji: '📊'
    },
    'LOW_STRESS': {
      bgColor: 'from-gray-50 to-slate-50',
      borderColor: 'border-gray-200',
      iconBg: 'bg-gray-100',
      iconColor: 'text-gray-600',
      textColor: 'text-gray-900', 
      emoji: '📉'
    },


    
    // ========== ZONA NEUTRA ==========
    'NEUTRAL': {
      bgColor: 'from-gray-50 to-slate-50',
      borderColor: 'border-gray-200',
      iconBg: 'bg-gray-100',
      iconColor: 'text-gray-600',
      textColor: 'text-gray-900',
      emoji: '⚪'
    },
    
    // ========== NÍVEIS DE SOBRECOMPRA (SID NEGATIVO) ==========
    'EXTREME_OVEREXTENDED': {
      bgColor: 'from-emerald-50 to-green-50',
      borderColor: 'border-emerald-200',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      textColor: 'text-emerald-900',
      emoji: '🛑'
    },
    'MAXIMUM_OVEREXTENDED': {
      bgColor: 'from-green-50 to-teal-50',
      borderColor: 'border-green-200',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      textColor: 'text-green-900',
      emoji: '⚠️'
    },
    'HIGH_OVEREXTENDED': {
      bgColor: 'from-teal-50 to-cyan-50',
      borderColor: 'border-teal-200',
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-600',
      textColor: 'text-teal-900',
      emoji: '📊'
    },
    'MODERATE_OVEREXTENDED': {
      bgColor: 'from-cyan-50 to-sky-50',
      borderColor: 'border-cyan-200',
      iconBg: 'bg-cyan-100',
      iconColor: 'text-cyan-600',
      textColor: 'text-cyan-900',
      emoji: '📈'
    },
    'LOW_OVEREXTENDED': {
      bgColor: 'from-sky-50 to-blue-50',
      borderColor: 'border-sky-200',
      iconBg: 'bg-sky-100',
      iconColor: 'text-sky-600',
      textColor: 'text-sky-900',
      emoji: '📉'
    },

    
    // ========== ESTADOS ESPECIAIS ==========
    'ERROR': {
      bgColor: 'from-gray-50 to-red-50',
      borderColor: 'border-gray-300',
      iconBg: 'bg-gray-100',
      iconColor: 'text-gray-600',
      textColor: 'text-gray-900',
      emoji: '❌'
    }
  };

  return styles[normalized] || styles['ERROR'];
}; 